/**
 * CollectionBulkAssignDialog — 2026-09-26 (user request): bulk assign PIC
 * Collection ke banyak invoice sekaligus DENGAN KONFIRMASI eksplisit.
 * Tidak ada auto-assign untuk data lama — PIC hanya ditetapkan lewat aksi ini
 * atau assign per baris. Semua perubahan tercatat di audit log (backend).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCheck, X } from "lucide-react";
import api, { formatDetail } from "@/lib/api";

export default function CollectionBulkAssignDialog({ items, onClose, onSaved }) {
  const [users, setUsers] = useState([]);
  const [pic, setPic] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/users").then(({ data }) => setUsers(Array.isArray(data) ? data : (data.items || []))).catch(() => {});
  }, []);

  const submit = async () => {
    if (!pic) { toast.error("Pilih PIC terlebih dahulu"); return; }
    const picName = users.find((u) => u.id === pic)?.name || pic;
    if (!window.confirm(
      `Assign ${picName} sebagai PIC Collection untuk ${items.length} invoice terpilih?\n\n` +
      `PIC existing pada invoice tsb akan diganti. Aksi tercatat di audit log per invoice.`
    )) return;
    setSaving(true);
    try {
      const { data } = await api.post("/collection/assignments/bulk", {
        items: items.map((i) => ({ project_id: i.project_id, entry_id: i.entry_id })),
        pic_id: pic,
        note: note || null,
      });
      if (data.failed > 0) {
        toast.warning(`${data.assigned} berhasil, ${data.failed} gagal.`);
      } else {
        toast.success(`PIC ${picName} ter-assign ke ${data.assigned} invoice.`);
      }
      onSaved?.();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Bulk assign gagal");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="bulk-assign-dialog">
      <div className="bg-white w-full max-w-md shadow-xl border border-slate-200">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display font-bold flex items-center gap-2"><UserCheck size={16} /> Bulk Assign PIC Collection</h3>
          <button type="button" onClick={onClose} data-testid="bulk-assign-cancel" className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 px-3 py-2" data-testid="bulk-assign-item-count">
            <b className="tabular-nums">{items.length}</b> invoice dipilih:
            <ul className="mt-1 space-y-0.5 text-[11px] text-slate-500 max-h-28 overflow-y-auto">
              {items.slice(0, 8).map((i) => <li key={`${i.project_id}::${i.entry_id}`}>· {i.label}</li>)}
              {items.length > 8 && <li className="italic">…dan {items.length - 8} lainnya</li>}
            </ul>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">PIC *</label>
            <select required data-testid="bulk-assign-pic-select" value={pic} onChange={(e) => setPic(e.target.value)}
              className="w-full border border-slate-300 px-2 py-1.5 text-sm">
              <option value="">— pilih PIC —</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.division} Lv{u.level}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Catatan (opsional)</label>
            <input type="text" data-testid="bulk-assign-note" value={note} onChange={(e) => setNote(e.target.value)}
              className="w-full border border-slate-300 px-2 py-1.5 text-sm" placeholder="Mis. pembagian area collection minggu ini" />
          </div>
          <p className="text-[10px] text-slate-500 italic">
            Hanya invoice terpilih yang diubah — tidak ada PIC otomatis untuk data lama lainnya.
          </p>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs border border-slate-300 hover:bg-slate-50">Batal</button>
          <button type="button" onClick={submit} disabled={saving || !pic} data-testid="bulk-assign-submit"
            className="px-3 py-1.5 text-xs bg-[#0B1D3A] hover:bg-[#12294f] text-white font-semibold disabled:opacity-40">
            {saving ? "Memproses…" : `Assign ke ${items.length} Invoice`}
          </button>
        </div>
      </div>
    </div>
  );
}
